import { useEffect, useState } from 'react';
import { Flame, ShieldCheck } from 'lucide-react';
import { fetchFieldPulse } from '../../api/dashboard';
import type { FieldPulseData } from '../../api/dashboard';

interface FieldPulseCardProps {
  /** Seçili proje varsa veri projeye göre süzülür. */
  projectId?: number;
}

/**
 * Dashboard "Saha Nabzı" kartı: günlük rapor serisi ve kazasız gün sayacı.
 * Mali takvim özeti ayrı MaliSummaryCard'a taşındı (2026-08-08).
 * Endpoint henüz yoksa veya istek başarısızsa kart gizlenir (dashboard'u bozmaz).
 */
export function FieldPulseCard({ projectId }: FieldPulseCardProps) {
  const [data, setData] = useState<FieldPulseData | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    fetchFieldPulse(projectId)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  if (failed) return null;

  return (
    <div className="rounded-xl bg-white p-5 shadow-sm" data-testid="dashboard-field-pulse">
      <div className="mb-4 flex items-center gap-2">
        <Flame className="h-5 w-5 text-orange-500" aria-hidden="true" />
        <h3 className="text-sm font-semibold text-slate-900">Saha Nabzı</h3>
      </div>

      {loading || !data ? (
        <div className="space-y-2" aria-busy="true">
          {Array.from({ length: 3 }, (_, i) => (
            <div key={i} className="h-8 animate-pulse rounded bg-slate-100" />
          ))}
        </div>
      ) : (
        <>
          <div className="mb-3 grid grid-cols-2 gap-2">
            <div className="rounded-lg bg-orange-50 p-3" data-testid="dashboard-field-pulse-streak">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase text-orange-600">
                <Flame className="h-3.5 w-3.5" aria-hidden="true" />
                Rapor Serisi
              </div>
              <div className="mt-1 text-sm font-bold text-orange-700">
                {data.report_streak_days > 0
                  ? `${data.report_streak_days} gün rapor serisi`
                  : 'Rapor serisi yok'}
              </div>
            </div>
            <div className="rounded-lg bg-emerald-50 p-3" data-testid="dashboard-field-pulse-incident">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase text-emerald-600">
                <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
                Sıfır Kaza
              </div>
              <div className="mt-1 text-sm font-bold text-emerald-700">
                {data.days_without_incident === null
                  ? 'Kaza kaydı yok'
                  : `${data.days_without_incident} gündür kazasız`}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
